// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

// Plugins
import {
  convertLength,
  formatNumber,
} from '../../../../../../plugins/general';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Components
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import {
  Title,
  TitleSize,
  TitleWeight,
} from '../../../../../../layout/elements/Title';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Types
import {
  LengthType,
  MeasurementType,
} from '../../../../../../types/global';

// View
import {
  LinerWearContainer,
  LinerWearContent,
  TitleContainer,
  MobileContainer,
  MobileContent,
  ParagraphUnitContainer,
} from './LinerWear.style';
import { LinerWearProps } from './LinerWear.types';

function LinerWear({
  className,
  unitData,
  testId,
}: LinerWearProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const {
    isXs,
    isSm,
    isMd,
    isLg,
  } = useBreakpoint();
  const isMobileOrTablet: boolean = isXs || isSm || isMd || isLg;

  const userLengthUnit: LengthType = getMeasurementUnit(MeasurementType.length);

  // Format the linerWear.value with unit
  const linerWearValue: string = formatNumber(
    convertLength(unitData?.linerWear, userLengthUnit).value || 0,
    2,
  );

  const paragraphSize: ParagraphSize = isXs ? ParagraphSize.sm : ParagraphSize.md;
  const hasLinerWear: boolean = !_.isNil(unitData?.linerWear);

  const renderValue = (): JSX.Element => {
    if (isXs) {
      return (
        <Paragraph
          testId={`${testId}-value`}
          weight={ParagraphWeight.medium}
        >
          {linerWearValue}
        </Paragraph>
      );
    }
    return (
      <Title
        testId={`${testId}-value`}
        size={TitleSize.xxs}
        weight={TitleWeight.medium}
      >
        {linerWearValue}
      </Title>
    );
  };

  const renderUnit = (isMobile: boolean): JSX.Element | null => {
    if (!hasLinerWear) {
      return null;
    }

    if (userLengthUnit === LengthType.inch) {
      if (isMobile && isXs) {
        return (
          <ParagraphUnitContainer>
            <Paragraph weight={ParagraphWeight.bold}>
              {userLengthUnit}
            </Paragraph>
          </ParagraphUnitContainer>
        );
      }
      return (
        <Title
          size={TitleSize.xxs}
          weight={TitleWeight.bold}
        >
          {userLengthUnit}
        </Title>
      );
    }

    return (
      <ParagraphUnitContainer>
        <Paragraph
          weight={ParagraphWeight.bold}
          size={isMobile ? paragraphSize : ParagraphSize.md}
        >
          {userLengthUnit}
        </Paragraph>
      </ParagraphUnitContainer>
    );
  };

  if (isMobileOrTablet) {
    return (
      <MobileContainer data-testid={testId} className={className}>
        <Paragraph
          testId={`${testId}-title`}
          weight={ParagraphWeight.bold}
          size={paragraphSize}
        >
          {t('view.crusher.linerWear')}
        </Paragraph>
        <MobileContent data-testid={`${testId}-content`}>
          {renderValue()}
          {renderUnit(true)}
        </MobileContent>
      </MobileContainer>
    );
  }

  return (
    <LinerWearContainer data-testid={testId} className={className}>
      <TitleContainer data-testid={`${testId}-title-container`}>
        <Paragraph
          testId={`${testId}-title`}
          weight={ParagraphWeight.bold}
        >
          {t('view.crusher.linerWear')}
        </Paragraph>
      </TitleContainer>
      <LinerWearContent data-testid={`${testId}-content`}>
        <Title
          testId={`${testId}-value`}
          size={TitleSize.xxs}
          weight={TitleWeight.medium}
        >
          {linerWearValue}
        </Title>
        {renderUnit(false)}
      </LinerWearContent>
    </LinerWearContainer>
  );
}

export { LinerWear };
